import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";

/** Simple canvas signature pad. Emits base64 PNG via onChange when the user lifts pen. */
export default function SignaturePad({ onChange, testId = "signature-pad" }) {
  const { lang } = useApp();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#F5F0E6";
    ctx.lineWidth = 2.2;
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: (t ? t.clientX : e.clientX) - rect.left,
      y: (t ? t.clientY : e.clientY) - rect.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    if (empty) setEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    onChange?.(null);
  };

  const label = {
    it: "Firma qui con il dito o il mouse",
    en: "Sign here with finger or mouse",
    he: "חתמו כאן באצבע או בעכבר",
    ru: "Подпишите пальцем или мышью",
  }[lang];

  return (
    <div>
      <p className="text-xs text-[#7A6E5A] mb-2">{label}</p>
      <div className="relative">
        <canvas
          ref={canvasRef}
          data-testid={testId}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="w-full h-40 rounded-md bg-white touch-none cursor-crosshair select-none"
        />
        {empty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="text-black/20 text-sm italic tracking-wide">✍  {label}</span>
          </div>
        )}
      </div>
      <div className="flex justify-end mt-2">
        <Button type="button" size="sm" variant="ghost" onClick={clear} data-testid="signature-clear"
          className="text-[#6B5F4C] hover:text-[#1A1610] hover:bg-[#EBE3D3] h-8">
          <Eraser className="h-3.5 w-3.5 me-1" />
          {t("signature_clear", lang)}
        </Button>
      </div>
    </div>
  );
}
